/**
 * The one renderer.
 *
 * `renderFrame` draws composition time `t` onto a 2D context. The live
 * preview and the exporter both call it, so what the vendor watches is the
 * frame that gets encoded — there is no second implementation to drift.
 *
 * It is synchronous and does no loading: media arrives through `FrameMedia`,
 * which hands back whatever is decoded right now (the exporter seeks and
 * waits before calling; the preview just draws what it has).
 */
import {
    ClipMotion,
    ImageLayer,
    LayerAnim,
    MainClip,
    OverlayLayer,
    ShapeLayer,
    TextLayer,
    TransitionType,
    VideoComposition,
    activeLayers,
    clamp01,
    easing,
    itemEnd,
    mainFrameState,
    mainTrack,
} from './composition';
import { cssFontStack } from './fonts';

export interface Drawable {
    source: CanvasImageSource;
    width: number;
    height: number;
}

export interface FrameMedia {
    /** The current picture of a main clip (a video element, an image, a poster). */
    clip(clip: MainClip): Drawable | null;
    /** A decoded image by URL (image layers, logos). */
    image(src: string): Drawable | null;
}

export interface LayerBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface RenderOptions {
    /** Filled with each visible overlay layer's box, in canvas pixels. */
    hitBoxes?: Map<string, LayerBox>;
}

type Ctx = CanvasRenderingContext2D;

const supportsFilter = (ctx: Ctx) => typeof (ctx as any).filter === 'string';

// ─── entry point ──────────────────────────────────────────────────────

export function renderFrame(
    ctx: Ctx,
    comp: VideoComposition,
    t: number,
    media: FrameMedia,
    options: RenderOptions = {},
): void {
    const W = ctx.canvas.width;
    const H = ctx.canvas.height;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = comp.background || '#000';
    ctx.fillRect(0, 0, W, H);

    drawMainTrack(ctx, comp, t, media, W, H);

    options.hitBoxes?.clear();
    for (const layer of activeLayers(comp, t)) {
        ctx.save();
        if (layer.kind === 'text') drawTextLayer(ctx, layer, t, W, H, options.hitBoxes);
        else if (layer.kind === 'image') drawImageLayer(ctx, layer, t, W, H, media, options.hitBoxes);
        else drawShapeLayer(ctx, layer, t, W, H, options.hitBoxes);
        ctx.restore();
    }
    ctx.restore();
}

// ─── main track ───────────────────────────────────────────────────────

function drawMainTrack(ctx: Ctx, comp: VideoComposition, t: number, media: FrameMedia, W: number, H: number) {
    const clips = mainTrack(comp).clips;
    const state = mainFrameState(clips, t);
    if (!state.current) return;
    const cur = state.current;
    const drawCur = () => drawClip(ctx, cur, t, media, W, H, comp.background);
    const tr = cur.transitionIn;
    if (!tr || state.transitionProgress >= 1) {
        drawCur();
        return;
    }
    const prev = state.previous;
    const fill = (color: string) => () => {
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, W, H);
    };
    const drawPrev = prev
        ? () => drawClip(ctx, prev, t, media, W, H, comp.background)
        : fill(tr.type === 'fade-white' || tr.type === 'flash' ? '#FFFFFF' : tr.type === 'fade-black' ? '#000000' : comp.background);
    drawTransition(ctx, tr.type, state.transitionProgress, drawPrev, drawCur, W, H);
}

/**
 * Draw one transition at raw progress p (0..1). Exported so the inspector's
 * transition tiles preview exactly what the export does.
 */
export function drawTransition(
    ctx: Ctx,
    type: TransitionType,
    p: number,
    drawPrev: () => void,
    drawCur: () => void,
    W: number,
    H: number,
): void {
    const e = easing.inOutCubic(clamp01(p));
    switch (type) {
        case 'soft-in': {
            // The incoming picture from its first frame: exposure lifts from
            // 85% and the frame settles from a slight scale. No black, ever.
            const s = easing.outCubic(clamp01(p));
            ctx.save();
            scaleAbout(ctx, 1.035 - 0.035 * s, W / 2, H / 2);
            drawCur();
            ctx.restore();
            veil(ctx, '#000000', 0.15 * (1 - s), W, H);
            return;
        }
        case 'crossfade': {
            drawPrev();
            ctx.save();
            ctx.globalAlpha *= e;
            drawCur();
            ctx.restore();
            return;
        }
        case 'fade-black':
        case 'fade-white': {
            const color = type === 'fade-black' ? '#000000' : '#FFFFFF';
            const s = easing.inOutSine(clamp01(p));
            if (s < 0.5) {
                drawPrev();
                veil(ctx, color, s * 2, W, H);
            } else {
                drawCur();
                veil(ctx, color, (1 - s) * 2, W, H);
            }
            return;
        }
        case 'slide-left':
        case 'slide-up': {
            const q = easing.inOutQuint(clamp01(p));
            const horizontal = type === 'slide-left';
            ctx.save();
            if (horizontal) ctx.translate(-q * W, 0);
            else ctx.translate(0, -q * H);
            drawPrev();
            ctx.restore();
            ctx.save();
            if (horizontal) ctx.translate((1 - q) * W, 0);
            else ctx.translate(0, (1 - q) * H);
            drawCur();
            ctx.restore();
            return;
        }
        case 'zoom': {
            ctx.save();
            scaleAbout(ctx, 1 + e * 0.35, W / 2, H / 2);
            drawPrev();
            ctx.restore();
            ctx.save();
            ctx.globalAlpha *= e;
            scaleAbout(ctx, 1.22 - 0.22 * easing.outCubic(clamp01(p)), W / 2, H / 2);
            drawCur();
            ctx.restore();
            return;
        }
        case 'blur': {
            const maxBlur = Math.max(W, H) * 0.018;
            const canBlur = supportsFilter(ctx);
            ctx.save();
            if (canBlur) (ctx as any).filter = `blur(${(e * maxBlur).toFixed(2)}px)`;
            drawPrev();
            ctx.restore();
            ctx.save();
            ctx.globalAlpha *= e;
            if (canBlur) (ctx as any).filter = `blur(${((1 - e) * maxBlur).toFixed(2)}px)`;
            drawCur();
            ctx.restore();
            return;
        }
        case 'wipe': {
            drawPrev();
            const edge = e * W;
            ctx.save();
            ctx.beginPath();
            ctx.rect(0, 0, edge, H);
            ctx.clip();
            drawCur();
            ctx.restore();
            if (e > 0 && e < 1) {
                // A soft shadow trailing the edge, so the wipe reads as a reveal, not a glitch.
                const soft = W * 0.035;
                const g = ctx.createLinearGradient(edge, 0, edge + soft, 0);
                g.addColorStop(0, 'rgba(0,0,0,0.28)');
                g.addColorStop(1, 'rgba(0,0,0,0)');
                ctx.save();
                ctx.fillStyle = g;
                ctx.fillRect(edge, 0, soft, H);
                ctx.fillStyle = 'rgba(255,255,255,0.55)';
                ctx.fillRect(edge - 1, 0, Math.max(1, W / 1080), H);
                ctx.restore();
            }
            return;
        }
        case 'iris': {
            drawPrev();
            if (e <= 0) return;
            // Reaches the corners at e = 1. The soft edge is two fainter rings
            // just outside the circle — no offscreen mask, so export stays cheap.
            const reach = Math.hypot(W, H) / 2 + 2;
            const r = e * reach;
            const soft = 1.25 * Math.max(1, Math.min(W, H) / 1080);
            const ring = (radius: number, alpha: number) => {
                if (radius <= 0) return;
                ctx.save();
                ctx.globalAlpha *= alpha;
                ctx.beginPath();
                ctx.arc(W / 2, H / 2, radius, 0, Math.PI * 2);
                ctx.clip();
                drawCur();
                ctx.restore();
            };
            if (e < 1) {
                ring(r + soft * 2, 0.3);
                ring(r + soft, 0.45);
            }
            ring(r, 1);
            return;
        }
        case 'flash': {
            // Asymmetric: the picture blows out over the first 40%, then the
            // incoming one decays out of white — reads as a camera flash, not a dip.
            const peak = 0.4;
            const q = clamp01(p);
            if (q < peak) {
                const a = q / peak;
                drawPrev();
                overexpose(ctx, drawPrev, a * 0.6);
                veil(ctx, '#FFFFFF', a * a, W, H);
            } else {
                const a = 1 - easing.outCubic((q - peak) / (1 - peak));
                drawCur();
                overexpose(ctx, drawCur, a * 0.5);
                veil(ctx, '#FFFFFF', a, W, H);
            }
            return;
        }
        case 'whip': {
            const c = clamp01(p);
            const q = easing.inOutCubic(c);
            // Blur follows the pan's speed (the easing's slope, 3 at its peak):
            // none at rest, a fifth of the frame mid-pan. The two pictures are
            // laid side by side, then smeared together so the seam blurs too —
            // that seam is what sells a whip-pan.
            const speed = (c < 0.5 ? 12 * c * c : 12 * (1 - c) * (1 - c)) / 3;
            const smear = speed * W * 0.2;
            ctx.save();
            ctx.translate(-q * W, 0);
            drawPrev();
            ctx.restore();
            ctx.save();
            ctx.translate((1 - q) * W, 0);
            drawCur();
            ctx.restore();
            if (smear >= 1) smearHorizontally(ctx, smear);
            return;
        }
        default:
            drawCur();
    }
}

/** Brighten what was just drawn by adding it on top of itself. */
function overexpose(ctx: Ctx, draw: () => void, amount: number) {
    if (amount <= 0.001) return;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha *= clamp01(amount);
    draw();
    ctx.restore();
}

/**
 * Horizontal motion blur of everything drawn so far, `length` px long.
 *
 * A canvas drawn onto itself shifted right at 50% doubles the number of
 * averaged copies, so k passes give a 2^k-tap box blur — a handful of draws
 * instead of hundreds of offset clip draws, and no visible ghost copies.
 *
 * The work happens on a scratch strip: the frame squeezed horizontally
 * (detail finer than the smear is gone anyway, so the kernel only needs
 * ~32 px there), padded on both sides with its own edge columns stretched
 * out, so the blur is centred and the frame edges smear like the rest
 * instead of staying sharp. Then it is stretched back over the frame.
 */
function smearHorizontally(ctx: Ctx, length: number) {
    const canvas = ctx.canvas as unknown as CanvasImageSource & { width: number; height: number };
    const W = canvas.width;
    const H = canvas.height;
    const squeeze = Math.max(1, length / 32);
    const sw = Math.max(1, Math.round(W / squeeze));
    const k = sw / W;
    const kernel = length * k;
    const pad = Math.ceil(kernel);
    const passes = Math.min(10, Math.ceil(Math.log2(Math.max(2, kernel))));
    const unit = kernel / 2 ** passes;
    const strip = scratchContext(ctx, pad + sw + pad, H);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (!strip) {
        // No DOM for a scratch canvas: smear in place at full size (edges stay sharp).
        const n = Math.min(10, Math.ceil(Math.log2(Math.max(2, length))));
        for (let i = 0; i < n; i++) {
            ctx.globalAlpha = 0.5;
            ctx.drawImage(canvas, (length / 2 ** n) * 2 ** i, 0);
        }
        ctx.restore();
        return;
    }
    strip.setTransform(1, 0, 0, 1, 0, 0);
    strip.globalAlpha = 1;
    strip.globalCompositeOperation = 'copy';
    strip.imageSmoothingEnabled = true;
    (strip as any).imageSmoothingQuality = 'high';
    strip.drawImage(canvas, 0, 0, W, H, pad, 0, sw, H);
    strip.globalCompositeOperation = 'source-over';
    // Clamp to edge: the first and last columns, stretched across the padding.
    strip.drawImage(canvas, 0, 0, 1, H, 0, 0, pad, H);
    strip.drawImage(canvas, W - 1, 0, 1, H, pad + sw, 0, pad, H);
    for (let i = 0; i < passes; i++) {
        strip.globalAlpha = 0.5;
        strip.drawImage(strip.canvas, unit * 2 ** i, 0);
    }
    // The kernel trails rightwards by ~kernel; read back from its middle.
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'copy';
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(strip.canvas, pad + kernel / 2, 0, sw, H, 0, 0, W, H);
    ctx.restore();
}

let scratch: HTMLCanvasElement | null = null;

/** A reusable offscreen 2D context of this size, or null where there is no DOM (tests). */
function scratchContext(ctx: Ctx, width: number, height: number): Ctx | null {
    const doc = (ctx.canvas as any)?.ownerDocument as Document | undefined;
    if (!doc?.createElement) return null;
    if (!scratch) scratch = doc.createElement('canvas');
    if (scratch.width !== width) scratch.width = width;
    if (scratch.height !== height) scratch.height = height;
    return scratch.getContext('2d');
}

function veil(ctx: Ctx, color: string, alpha: number, W: number, H: number) {
    if (alpha <= 0) return;
    ctx.save();
    ctx.globalAlpha *= clamp01(alpha);
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
}

function scaleAbout(ctx: Ctx, s: number, cx: number, cy: number) {
    ctx.translate(cx, cy);
    ctx.scale(s, s);
    ctx.translate(-cx, -cy);
}

function drawClip(ctx: Ctx, clip: MainClip, t: number, media: FrameMedia, W: number, H: number, bg: string) {
    if (clip.kind === 'color') {
        // A colour card: no media to wait for, the whole frame is the fill.
        ctx.fillStyle = clip.color || bg;
        ctx.fillRect(0, 0, W, H);
        return;
    }
    const d = media.clip(clip);
    const progress = clip.duration > 0 ? clamp01((t - clip.start) / clip.duration) : 0;
    if (!d || !d.width || !d.height) {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, W, H);
        return;
    }
    if (clip.fit === 'contain') {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, W, H);
    }
    drawMedia(ctx, d, W, H, clip.fit, clip.motion, progress);
}

/** Cover/contain + Ken Burns. Exported for the bin's previews. */
export function drawMedia(
    ctx: Ctx,
    d: Drawable,
    W: number,
    H: number,
    fit: 'cover' | 'contain',
    motion: ClipMotion | undefined,
    progress: number,
): void {
    const base = fit === 'contain' ? Math.min(W / d.width, H / d.height) : Math.max(W / d.width, H / d.height);
    let s = 1;
    let dx = 0;
    let dy = 0;
    if (motion && motion.type !== 'none' && motion.intensity > 0) {
        const k = clamp01(motion.intensity);
        const e = easing.inOutSine(clamp01(progress));
        const zoom = 0.04 + k * 0.2;
        const pan = 0.06 + k * 0.16;
        switch (motion.type) {
            case 'zoom-in': s = 1 + zoom * e; break;
            case 'zoom-out': s = 1 + zoom * (1 - e); break;
            case 'pan-left': s = 1 + pan; dx = (0.5 - e) * pan * W; break;
            case 'pan-right': s = 1 + pan; dx = (e - 0.5) * pan * W; break;
            case 'pan-up': s = 1 + pan; dy = (0.5 - e) * pan * H; break;
        }
    }
    const dw = d.width * base * s;
    const dh = d.height * base * s;
    ctx.drawImage(d.source, (W - dw) / 2 + dx, (H - dh) / 2 + dy, dw, dh);
}

// ─── layer animation ──────────────────────────────────────────────────

export interface AnimState {
    alpha: number;
    dx: number;
    dy: number;
    scale: number;
    blur: number;
    /** 0..1 left-to-right reveal. */
    reveal: number;
    /** Fraction of characters shown (typewriter), 1 = all. */
    chars: number;
    /** Raw progress for per-letter stagger, or null when not staggering. */
    stagger: number | null;
    /** Raw progress of the per-line mask reveal (1 = settled), or null. */
    maskUp: number | null;
    /** Raw progress of the per-word stagger (1 = all words in), or null. */
    words: number | null;
    /** Whether maskUp/words came from an entrance (eases differently from an exit). */
    staggerIn: boolean;
    /** Extra letter spacing in em on top of the layer's own (tracking-in). */
    tracking: number;
}

export function layerAnimState(layer: OverlayLayer, t: number, W: number, H: number): AnimState {
    const s: AnimState = {
        alpha: 1, dx: 0, dy: 0, scale: 1, blur: 0, reveal: 1, chars: 1, stagger: null,
        maskUp: null, words: null, staggerIn: true, tracking: 0,
    };
    const ad = Math.max(0.01, Math.min(layer.animDuration || 0.6, layer.duration / 2));
    const pIn = clamp01((t - layer.start) / ad);
    const pOut = clamp01((itemEnd(layer) - t) / ad);
    if (pIn < 1) applyAnim(layer.animIn, pIn, s, W, H, true, layer.kind === 'text');
    if (pOut < 1) applyAnim(layer.animOut, pOut, s, W, H, false, layer.kind === 'text');
    return s;
}

function applyAnim(kind: LayerAnim, p: number, s: AnimState, W: number, H: number, isIn: boolean, isText: boolean) {
    if (kind === 'none') return;
    // Arrivals decelerate (expo-out); departures ease both ends.
    const move = isIn ? easing.outExpo(p) : easing.inOutSine(p);
    const fade = isIn ? easing.outCubic(p) : easing.inOutSine(p);
    const dir = isIn ? 1 : -1;
    switch (kind) {
        case 'fade':
            s.alpha *= fade;
            break;
        case 'rise':
            s.alpha *= fade;
            s.dy += (1 - move) * 0.035 * H * dir;
            break;
        case 'fall':
            s.alpha *= fade;
            s.dy -= (1 - move) * 0.035 * H * dir;
            break;
        case 'blur':
            s.alpha *= fade;
            s.blur += (1 - move) * 0.012 * H;
            break;
        case 'scale':
            s.alpha *= fade;
            s.scale *= 0.88 + 0.12 * move;
            break;
        case 'slide-left':
            s.alpha *= fade;
            s.dx += (1 - move) * 0.07 * W * dir;
            break;
        case 'wipe':
            s.reveal = Math.min(s.reveal, isIn ? easing.inOutCubic(p) : 1);
            if (!isIn) s.alpha *= fade;
            break;
        case 'typewriter':
            if (isText && isIn) s.chars = Math.min(s.chars, p);
            else s.alpha *= fade;
            break;
        case 'letter-stagger':
            if (isText && isIn) s.stagger = p;
            else s.alpha *= fade;
            break;
        // The next two are staggered per line / per word in drawTextLayer;
        // an exit runs the same reveal backwards (p falls from 1 to 0).
        case 'mask-up':
            if (isText) {
                s.maskUp = Math.min(s.maskUp ?? 1, p);
                s.staggerIn = isIn;
            } else s.alpha *= fade;
            break;
        case 'word-stagger':
            if (isText) {
                s.words = Math.min(s.words ?? 1, p);
                s.staggerIn = isIn;
            } else s.alpha *= fade;
            break;
        case 'tracking-in': {
            // Slow settle: most of the tightening early, the last bit lingering.
            const settle = isIn ? easing.outQuart(p) : easing.inOutSine(p);
            s.alpha *= fade;
            if (isText) s.tracking += (1 - settle) * 0.55;
            else s.scale *= 0.94 + 0.06 * settle;
            break;
        }
    }
}

function applyLayerTransform(ctx: Ctx, cx: number, cy: number, scale: number, rotation: number) {
    if (scale === 1 && !rotation) return;
    ctx.translate(cx, cy);
    if (rotation) ctx.rotate((rotation * Math.PI) / 180);
    if (scale !== 1) ctx.scale(scale, scale);
    ctx.translate(-cx, -cy);
}

function applyReveal(ctx: Ctx, reveal: number, x: number, y: number, w: number, h: number) {
    if (reveal >= 1) return;
    ctx.beginPath();
    ctx.rect(x, y - h, w * reveal, h * 3);
    ctx.clip();
}

// ─── text ─────────────────────────────────────────────────────────────

interface LaidLine {
    text: string;
    /** x offset of each character from the line start, px. */
    xs: number[];
    width: number;
}

interface TextLayout {
    lines: LaidLine[];
    fontPx: number;
    lineHeightPx: number;
    font: string;
    spacingPx: number;
    totalChars: number;
}

const layoutCache = new Map<string, TextLayout>();

export function fontString(layer: Pick<TextLayer, 'fontStyle' | 'fontWeight' | 'fontFamily'>, px: number): string {
    return `${layer.fontStyle === 'italic' ? 'italic ' : ''}${layer.fontWeight || 400} ${px.toFixed(2)}px ${cssFontStack(layer.fontFamily)}`;
}

function layoutText(ctx: Ctx, layer: TextLayer, W: number, H: number): TextLayout {
    const fontPx = Math.max(1, layer.fontSize * H);
    const spacingPx = (layer.letterSpacing || 0) * fontPx;
    const padX = layer.background ? layer.background.paddingX * fontPx : 0;
    const maxWidth = Math.max(fontPx, layer.width * W - padX * 2);
    const raw = layer.uppercase ? layer.text.toUpperCase() : layer.text;
    const font = fontString(layer, fontPx);
    const key = `${font}|${spacingPx}|${maxWidth}|${raw}`;
    const hit = layoutCache.get(key);
    if (hit) return hit;

    ctx.save();
    ctx.font = font;
    const measure = (s: string) => (s ? ctx.measureText(s).width + spacingPx * (Array.from(s).length - 1) : 0);
    const lines: LaidLine[] = [];
    for (const para of raw.split('\n')) {
        const words = para.split(/(\s+)/).filter((w) => w.length);
        let line = '';
        for (const w of words) {
            const candidate = line + w;
            if (line && measure(candidate.trimEnd()) > maxWidth && w.trim()) {
                lines.push(laidLine(ctx, line.trimEnd(), spacingPx));
                line = w.trimStart();
            } else {
                line = candidate;
            }
        }
        lines.push(laidLine(ctx, line.trim(), spacingPx));
    }
    ctx.restore();
    const layout: TextLayout = {
        lines,
        fontPx,
        lineHeightPx: fontPx * (layer.lineHeight || 1.2),
        font,
        spacingPx,
        totalChars: lines.reduce((n, l) => n + Array.from(l.text).length, 0),
    };
    if (layoutCache.size > 400) layoutCache.clear();
    layoutCache.set(key, layout);
    return layout;
}

function laidLine(ctx: Ctx, text: string, spacingPx: number): LaidLine {
    const chars = Array.from(text);
    const xs: number[] = [];
    let prefix = '';
    for (let i = 0; i < chars.length; i++) {
        // Measuring the prefix keeps the font's kerning between pairs.
        xs.push(ctx.measureText(prefix).width + spacingPx * i);
        prefix += chars[i];
    }
    const width = chars.length ? ctx.measureText(text).width + spacingPx * (chars.length - 1) : 0;
    return { text, xs, width };
}

/** Clear cached text layouts (after a web font finishes loading). */
export function invalidateTextLayouts() {
    layoutCache.clear();
}

function rgba(color: string, alpha: number): string {
    const hex = color.trim();
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
    if (!m) return color;
    let h = m[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${clamp01(alpha)})`;
}

function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number) {
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    ctx.lineTo(x + rr, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
    ctx.lineTo(x, y + rr);
    ctx.quadraticCurveTo(x, y, x + rr, y);
    ctx.closePath();
}

function drawTextLayer(ctx: Ctx, layer: TextLayer, t: number, W: number, H: number, boxes?: Map<string, LayerBox>) {
    if (!layer.text) return;
    const anim = layerAnimState(layer, t, W, H);
    const alpha = clamp01(layer.opacity ?? 1) * anim.alpha;
    if (alpha <= 0.001) return;
    const L = layoutText(ctx, layer, W, H);
    const cx = layer.x * W + anim.dx;
    const cy = layer.y * H + anim.dy;
    const boxW = layer.width * W;
    const blockH = L.lines.length * L.lineHeightPx;
    const top = cy - blockH / 2;
    const padX = layer.background ? layer.background.paddingX * L.fontPx : 0;
    const padY = layer.background ? layer.background.paddingY * L.fontPx : 0;
    // tracking-in widens the set spacing without re-wrapping: each character
    // moves out by `extra` per index and the line re-centres on its new width.
    const extra = anim.tracking * L.fontPx;
    const lineW = (line: LaidLine) => line.width + extra * Math.max(0, line.xs.length - 1);
    const lineX = (line: LaidLine) => {
        if (layer.align === 'left') return cx - boxW / 2 + padX;
        if (layer.align === 'right') return cx + boxW / 2 - padX - lineW(line);
        return cx - lineW(line) / 2;
    };
    const minX = Math.min(...L.lines.map(lineX));
    const maxX = Math.max(...L.lines.map((l) => lineX(l) + lineW(l)));

    applyLayerTransform(ctx, cx, cy, anim.scale, layer.rotation || 0);
    if (anim.blur > 0.05 && supportsFilter(ctx)) (ctx as any).filter = `blur(${anim.blur.toFixed(2)}px)`;
    const bx = minX - padX;
    const by = top - padY;
    const bw = maxX - minX + padX * 2;
    const bh = blockH + padY * 2;
    applyReveal(ctx, anim.reveal, bx, by, bw, bh);
    boxes?.set(layer.id, { x: bx, y: by, width: bw, height: bh });

    if (layer.background && layer.background.opacity > 0) {
        ctx.save();
        ctx.globalAlpha = alpha * clamp01(layer.background.opacity);
        ctx.fillStyle = layer.background.color;
        const radius = layer.background.style === 'pill' ? bh / 2 : L.fontPx * 0.14;
        roundRect(ctx, bx, by, bw, bh, radius);
        ctx.fill();
        ctx.restore();
    }

    ctx.font = L.font;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillStyle = layer.color || '#FFFFFF';
    const shadowCss = layer.shadow && layer.shadow.opacity > 0 ? rgba(layer.shadow.color, layer.shadow.opacity) : null;
    if (shadowCss && layer.shadow) {
        ctx.shadowColor = shadowCss;
        ctx.shadowBlur = layer.shadow.blur * L.fontPx;
        ctx.shadowOffsetY = layer.shadow.offsetY * L.fontPx;
    }
    const glyph = glyphPainter(ctx, layer, L.fontPx, shadowCss);
    const visibleChars = anim.chars >= 1 ? Infinity : Math.floor(anim.chars * L.totalChars + 1e-6);
    const n = L.totalChars;
    const lineCount = L.lines.length;
    const wordCount = anim.words !== null ? L.lines.reduce((k, l) => k + l.text.split(/\s+/).filter(Boolean).length, 0) : 0;
    let index = 0;
    let word = -1;
    L.lines.forEach((line, li) => {
        const x0 = lineX(line);
        // 'middle' sits a touch high for most faces; nudge to the optical centre.
        let y = top + L.lineHeightPx * (li + 0.5) + L.fontPx * 0.04;
        const chars = Array.from(line.text);
        let masked = false;
        if (anim.maskUp !== null && anim.maskUp < 1) {
            // Lines rise in turn from behind an invisible edge just under their
            // own descenders; the glyphs start a full em-and-a-half below it.
            const window = lineCount > 1 ? 0.7 : 1;
            const startAt = lineCount > 1 ? (li / (lineCount - 1)) * (1 - window) : 0;
            const lp = clamp01((anim.maskUp - startAt) / window);
            if (lp <= 0) {
                index += chars.length;
                word += line.text.split(/\s+/).filter(Boolean).length;
                return;
            }
            const e = anim.staggerIn ? easing.outQuart(lp) : easing.inOutCubic(lp);
            const edge = y + L.fontPx * 0.65;
            ctx.save();
            masked = true;
            ctx.beginPath();
            ctx.rect(minX - L.fontPx, edge - L.lineHeightPx - L.fontPx * 2, maxX - minX + L.fontPx * 2, L.lineHeightPx + L.fontPx * 2);
            ctx.clip();
            y += (1 - e) * L.fontPx * 1.45;
        }
        let prevSpace = true;
        for (let i = 0; i < chars.length; i++, index++) {
            if (index >= visibleChars) break;
            const ch = chars[i];
            const space = /\s/.test(ch);
            if (!space && prevSpace) word++;
            prevSpace = space;
            if (space) continue;
            const x = x0 + line.xs[i] + extra * i;
            if (anim.stagger !== null) {
                const window = 0.42;
                const startAt = n > 1 ? (index / (n - 1)) * (1 - window) : 0;
                const lp = clamp01((anim.stagger - startAt) / window);
                if (lp <= 0) continue;
                glyph(ch, x, y + (1 - easing.outExpo(lp)) * L.fontPx * 0.45, alpha * easing.outCubic(lp));
            } else if (anim.words !== null && anim.words < 1) {
                // Fewer words get a longer, overlapping fade each; long lines tick along faster.
                const window = wordCount > 1 ? Math.max(0.28, 0.6 - 0.04 * (wordCount - 2)) : 1;
                const startAt = wordCount > 1 ? (word / (wordCount - 1)) * (1 - window) : 0;
                const lp = clamp01((anim.words - startAt) / window);
                if (lp <= 0) continue;
                const a = anim.staggerIn ? easing.outCubic(lp) : easing.inOutSine(lp);
                const rise = anim.staggerIn ? easing.outExpo(lp) : easing.inOutSine(lp);
                glyph(ch, x, y + (1 - rise) * L.fontPx * 0.3, alpha * a);
            } else {
                glyph(ch, x, y, alpha);
            }
        }
        if (masked) ctx.restore();
    });
}

/**
 * Paints one character with the layer's fill and outline.
 *
 * - Solid fill + outline: the stroke goes UNDER the fill at twice the width,
 *   so the outline sits outside the letter and the counters stay clean.
 * - Translucent or no fill (outline-only captions): fill first, then the
 *   stroke on top at its own width, centred on the contour.
 * A shadow is cast once, by the first pass, so it never darkens the outline.
 */
function glyphPainter(ctx: Ctx, layer: TextLayer, fontPx: number, shadowCss: string | null) {
    const fillA = clamp01(layer.fillOpacity ?? 1);
    const stroke = layer.stroke && layer.stroke.width > 0 ? layer.stroke : null;
    const under = !!stroke && fillA >= 0.999;
    if (stroke) {
        ctx.strokeStyle = stroke.color || layer.color || '#FFFFFF';
        ctx.lineWidth = Math.max(0.5, stroke.width * fontPx * (under ? 2 : 1));
        ctx.lineJoin = 'round';
        ctx.miterLimit = 2;
    }
    const noShadow = () => { if (shadowCss) ctx.shadowColor = 'transparent'; };
    const withShadow = () => { if (shadowCss) ctx.shadowColor = shadowCss; };
    return (ch: string, x: number, y: number, a: number) => {
        if (!stroke) {
            ctx.globalAlpha = a * fillA;
            ctx.fillText(ch, x, y);
            return;
        }
        if (under) {
            ctx.globalAlpha = a;
            ctx.strokeText(ch, x, y);
            noShadow();
            ctx.fillText(ch, x, y);
            withShadow();
            return;
        }
        let shadowed = false;
        if (fillA > 0.001) {
            ctx.globalAlpha = a * fillA;
            ctx.fillText(ch, x, y);
            noShadow();
            shadowed = true;
        }
        ctx.globalAlpha = a;
        ctx.strokeText(ch, x, y);
        if (shadowed) withShadow();
    };
}

// ─── image + shape layers ─────────────────────────────────────────────

function drawImageLayer(
    ctx: Ctx,
    layer: ImageLayer,
    t: number,
    W: number,
    H: number,
    media: FrameMedia,
    boxes?: Map<string, LayerBox>,
) {
    const d = media.image(layer.src);
    const aspect = d ? d.width / d.height : layer.aspect || 1;
    const anim = layerAnimState(layer, t, W, H);
    const alpha = clamp01(layer.opacity ?? 1) * anim.alpha;
    const w = layer.width * W;
    const h = w / aspect;
    const cx = layer.x * W + anim.dx;
    const cy = layer.y * H + anim.dy;
    const x = cx - w / 2;
    const y = cy - h / 2;
    boxes?.set(layer.id, { x, y, width: w, height: h });
    if (!d || alpha <= 0.001) return;
    applyLayerTransform(ctx, cx, cy, anim.scale, layer.rotation || 0);
    if (anim.blur > 0.05 && supportsFilter(ctx)) (ctx as any).filter = `blur(${anim.blur.toFixed(2)}px)`;
    applyReveal(ctx, anim.reveal, x, y, w, h);
    ctx.globalAlpha = alpha;
    ctx.drawImage(d.source, x, y, w, h);
}

function drawShapeLayer(ctx: Ctx, layer: ShapeLayer, t: number, W: number, H: number, boxes?: Map<string, LayerBox>) {
    const anim = layerAnimState(layer, t, W, H);
    const alpha = clamp01(layer.opacity ?? 1) * anim.alpha;
    const w = layer.width * W;
    const h = layer.shape === 'line' ? Math.max(1, layer.height * H) : layer.height * H;
    const cx = layer.x * W + anim.dx;
    const cy = layer.y * H + anim.dy;
    const x = cx - w / 2;
    const y = cy - h / 2;
    boxes?.set(layer.id, { x, y, width: w, height: Math.max(h, 8) });
    if (alpha <= 0.001) return;
    applyLayerTransform(ctx, cx, cy, anim.scale, layer.rotation || 0);
    if (anim.blur > 0.05 && supportsFilter(ctx)) (ctx as any).filter = `blur(${anim.blur.toFixed(2)}px)`;
    applyReveal(ctx, anim.reveal, x, y, w, h);
    if (layer.shape === 'scrim') {
        const toBottom = layer.direction !== 'to-top';
        const g = ctx.createLinearGradient(0, toBottom ? y : y + h, 0, toBottom ? y + h : y);
        // An eased ramp — a straight one shows a visible band where it starts.
        for (let i = 0; i <= 6; i++) {
            const stop = i / 6;
            g.addColorStop(stop, rgba(layer.color, alpha * easing.inOutSine(stop)));
        }
        ctx.fillStyle = g;
        ctx.fillRect(x, y, w, h);
        return;
    }
    ctx.globalAlpha = alpha;
    ctx.fillStyle = layer.color;
    roundRect(ctx, x, y, w, h, (layer.radius || 0) * W);
    ctx.fill();
}
